import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { searchHelp } from '../lib/helpContent';
import { HELP_SECTIONS_FR } from '../lib/helpContent.fr';
import { HELP_SECTIONS_ES } from '../lib/helpContent.es';
import PageHeader from '../components/PageHeader';
import { withPageIcon } from '../lib/uiIcons';

export default function HelpPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [query, setQuery] = useState('');
  const [activeId, setActiveId] = useState<string>('');

  // Pick the help content in the active language (EN default, FR/ES if selected).
  const lang = (i18n.language || 'en').toLowerCase();
  const sections = lang.startsWith('fr') ? HELP_SECTIONS_FR : lang.startsWith('es') ? HELP_SECTIONS_ES : undefined;
  // useMemo deps: sections must be stable (HELP_SECTIONS_FR is module-scoped,
  // identical reference every render), so results only recompute on query change.
  const results = useMemo(() => searchHelp(query, sections), [query, sections]);

  // Jump to the first section on first render (or when language switches).
  const resolvedActiveId = activeId || results[0]?.section.id || '';
  const active = results.find((r) => r.section.id === resolvedActiveId) ?? results[0];

  return (
    <div className="page" data-page="help">
      <PageHeader
        title={withPageIcon('helpPage.title', t('helpPage.title'))}
        subtitle={t('helpPage.subtitle')}
        actions={
          <button className="btn btn-primary" onClick={() => navigate('/welcome')}>
            {t('helpPage.restartTour')}
          </button>
        }
      />

      <div
        style={{
          display: 'flex',
          gap: 20,
          alignItems: 'flex-start',
          marginBottom: 20,
        }}
      >
        <input
          className="form-input"
          style={{ flex: 1, maxWidth: 520 }}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('helpPage.searchPlaceholder')}
          autoFocus
        />
        {query.trim() && (
          <span className="text-dim" style={{ alignSelf: 'center', fontSize: 'var(--font-sm)' }}>
            {t('helpPage.sections', { count: results.length })}
          </span>
        )}
      </div>

      <div className="help-layout" style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
        {/* Section index */}
        <nav className="help-nav" style={{ flex: '0 0 240px', position: 'sticky', top: 12 }}>
          {results.map(({ section }) => (
            <button
              key={section.id}
              type="button"
              className={`btn btn-small help-nav-item ${section.id === resolvedActiveId ? 'btn-primary' : ''}`}
              style={{ display: 'flex', width: '100%', justifyContent: 'flex-start', textAlign: 'left', marginBottom: 4 }}
              onClick={() => { setActiveId(section.id); setQuery(''); }}
            >
              <span style={{ marginRight: 6 }}>{section.icon}</span>
              <span style={{ flex: 1 }}>{section.title}</span>
            </button>
          ))}
          {results.length === 0 && (
            <p className="text-dim" style={{ fontSize: 'var(--font-sm)', padding: '4px 2px' }}>
              {t('helpPage.noMatch')}
            </p>
          )}
        </nav>

        {/* Active section content */}
        <div className="help-content" style={{ flex: 1, minWidth: 0 }}>
          {active && (
            <>
              <h2 style={{ marginTop: 0 }}>
                {active.section.icon} {active.section.title}
              </h2>
              <p className="text-dim" style={{ marginTop: -6 }}>{active.section.summary}</p>
              {active.subsections.map((sub) => (
                <div key={sub.heading} className="card" style={{ padding: '14px 16px', marginBottom: 14 }}>
                  <h3 style={{ marginTop: 0, marginBottom: 8 }}>{sub.heading}</h3>
                  {sub.body.map((p, i) => (
                    <p key={i} style={{ margin: '0 0 8px', lineHeight: 1.55 }}>{p}</p>
                  ))}
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
